import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  createRoster,
  deleteRoster,
  getPmUsers,
  listRosters,
  listShiftTemplates,
} from '@/services/api.client';

interface FormState {
  templateId: string;
  guardUserId: string;
  effectiveFrom: string;
  effectiveUntil: string;
}

const EMPTY_FORM: FormState = {
  templateId: '',
  guardUserId: '',
  effectiveFrom: '',
  effectiveUntil: '',
};

interface Props {
  propertyId: string;
  isPm: boolean;
}

export function RosterList({ propertyId, isPm }: Props) {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const { data: rosters, isLoading } = useQuery({
    queryKey: ['rosters', propertyId],
    queryFn: () => listRosters(propertyId),
    enabled: !!propertyId,
  });

  const { data: templatesData } = useQuery({
    queryKey: ['shift-templates', propertyId],
    queryFn: () => listShiftTemplates(propertyId),
    enabled: dialogOpen,
  });

  const { data: usersData } = useQuery({
    queryKey: ['pm-users', propertyId, 'guard'],
    queryFn: () => getPmUsers(propertyId, { role: 'guard', perPage: 100 }),
    enabled: dialogOpen,
  });

  // Only show roster-only guards (Mode B) — credential guards (Mode A) manage their own shifts
  const guards = (usersData?.data ?? []).filter((u) => u.authMode === 'roster_only');

  const createMutation = useMutation({
    mutationFn: () =>
      createRoster(propertyId, {
        templateId: form.templateId,
        guardUserId: form.guardUserId,
        effectiveFrom: form.effectiveFrom,
        ...(form.effectiveUntil ? { effectiveUntil: form.effectiveUntil } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rosters', propertyId] });
      toast.success('Guard assigned');
      setDialogOpen(false);
      setForm(EMPTY_FORM);
    },
    onError: () => toast.error('Failed to assign guard'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteRoster(propertyId, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rosters', propertyId] });
      toast.success('Assignment removed');
    },
    onError: () => toast.error('Failed to remove assignment'),
  });

  return (
    <div className="space-y-4">
      {isPm && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setDialogOpen(true)}>
            Assign Guard
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Guard</TableHead>
              <TableHead>Shift</TableHead>
              <TableHead>Effective From</TableHead>
              <TableHead>Until</TableHead>
              {isPm && <TableHead className="w-16">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rosters?.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>{entry.guardName}</TableCell>
                <TableCell>{entry.templateName}</TableCell>
                <TableCell>{entry.effectiveFrom}</TableCell>
                <TableCell>{entry.effectiveUntil ?? '—'}</TableCell>
                {isPm && (
                  <TableCell>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" aria-label="Remove">
                          <Trash2 className="size-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove assignment?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will remove {entry.guardName} from the {entry.templateName} roster.
                            This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteMutation.mutate(entry.id)}>
                            Remove
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setForm(EMPTY_FORM);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Guard to Shift</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="roster-template">Template</Label>
              <select
                id="roster-template"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.templateId}
                onChange={(e) => setForm((f) => ({ ...f, templateId: e.target.value }))}
              >
                <option value="">Select shift template…</option>
                {(templatesData ?? []).map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="roster-guard">Guard</Label>
              <select
                id="roster-guard"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.guardUserId}
                onChange={(e) => setForm((f) => ({ ...f, guardUserId: e.target.value }))}
              >
                <option value="">Select guard…</option>
                {guards.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.fullName}
                  </option>
                ))}
              </select>
              {usersData && guards.length === 0 && (
                <p className="text-xs text-[var(--text-tertiary)]">
                  No roster guards found. Use &quot;Add to Roster&quot; on the Guards tab first.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="roster-from">Effective from</Label>
                <Input
                  id="roster-from"
                  type="date"
                  value={form.effectiveFrom}
                  onChange={(e) => setForm((f) => ({ ...f, effectiveFrom: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="roster-until">Effective until</Label>
                <Input
                  id="roster-until"
                  type="date"
                  value={form.effectiveUntil}
                  onChange={(e) => setForm((f) => ({ ...f, effectiveUntil: e.target.value }))}
                  placeholder="Open-ended"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
